import React from 'react';

import { DropdownChevron } from 'components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { DropdownHeaderTrigger } from 'components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { DropdownMenu } from 'components/Dropdown/menus/Menu/DropdownMenu';
import { DropdownMenuItem } from 'components/Dropdown/menus/Menu/Item/DropdownMenuItem';
import { Icon } from 'components/Icon';
import { sumBy } from 'utils/functional/array/sumBy';

import {
  CartPillCount,
  CartPillCountAndIconHolder,
  CartPillMenuItemCount,
  CartPillMenuItemEmptyMessage,
  CartPillMenuItemLink,
} from './CartPill.styled';
import { CartPillOrgData } from './CartPillOrgData';

const cartPillQaId = 'cart-pill';
const cartPillCountQaId = 'cart-pill-count';

type Props = {
  selectedOrgId: string | undefined;
  getOrgCartDataHref: (orgCartData: CartPillOrgData) => string;
  orgsCartData: CartPillOrgData[];
  emptyHrefs: {
    postAJob: string;
    postAnInternship: string;
    postAVolop: string;
  };
};

export function CartPill({
  selectedOrgId,
  getOrgCartDataHref,
  orgsCartData,
  emptyHrefs,
}: Props) {
  const totalItemsCount = sumBy(orgsCartData, 'itemsCount');

  const filteredOrgsCartData = orgsCartData.filter(
    (orgCartData) => orgCartData.itemsCount > 0,
  );

  const countAndIcon = (
    <CartPillCountAndIconHolder>
      {totalItemsCount > 0 && (
        <CartPillCount data-qa-id={cartPillCountQaId}>
          {totalItemsCount}
        </CartPillCount>
      )}

      <Icon name="cart" size={18} />
    </CartPillCountAndIconHolder>
  );

  if (orgsCartData.length === 1) {
    return (
      <DropdownHeaderTrigger
        variant="blue"
        minWidth={51}
        qaId={cartPillQaId}
        asRouterLink={{ to: getOrgCartDataHref(orgsCartData[0]) }}
        smallHorizontalPadding
      >
        {countAndIcon}
      </DropdownHeaderTrigger>
    );
  }

  const isEmpty = filteredOrgsCartData.length === 0;

  return (
    <Dropdown
      trigger={
        <DropdownHeaderTrigger
          variant={isEmpty ? 'grey' : 'blue'}
          minWidth={51}
          smallHorizontalPadding
          roundBottomWhenOpen
        >
          {countAndIcon}

          <DropdownChevron />
        </DropdownHeaderTrigger>
      }
      qaId={cartPillQaId}
    >
      <DropdownMenu width={isEmpty ? 180 : 147}>
        {isEmpty && (
          <>
            <DropdownMenuItem>
              <CartPillMenuItemEmptyMessage>
                All your carts are empty
              </CartPillMenuItemEmptyMessage>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartPillMenuItemLink
                to={emptyHrefs.postAJob}
                data-qa-id="cart-pill-menu-post-a-job"
              >
                {getText('Post a Job')}
              </CartPillMenuItemLink>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartPillMenuItemLink
                to={emptyHrefs.postAnInternship}
                data-qa-id="cart-pill-menu-post-an-internship"
              >
                {getText('Post an Internship')}
              </CartPillMenuItemLink>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartPillMenuItemLink
                to={emptyHrefs.postAVolop}
                data-qa-id="cart-pill-menu-post-a-volop"
              >
                {getText('Post a Volunteer Opportunity')}
              </CartPillMenuItemLink>
            </DropdownMenuItem>
          </>
        )}

        {filteredOrgsCartData.map((orgCartData) => (
          <DropdownMenuItem
            key={orgCartData.id}
            qaId={
              orgCartData.id === selectedOrgId
                ? 'cart-pill-menu-item-selected'
                : undefined
            }
          >
            <CartPillMenuItemLink
              to={getOrgCartDataHref(orgCartData)}
              $isSelected={orgCartData.id === selectedOrgId}
            >
              <CartPillMenuItemCount data-qa-id="cart-pill-menu-item-count">
                {orgCartData.itemsCount}
              </CartPillMenuItemCount>

              <span data-qa-id="cart-pill-menu-item-org-name">
                {orgCartData.name}
              </span>
            </CartPillMenuItemLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
